import { MouseEvent, useEffect, useRef } from 'react';
import { EnterToSubmitMode, useEnterToSubmit } from './useEnterToSubmit';

type Options = {
  /** Submit mode. Defaults to `auto`. */
  mode?: EnterToSubmitMode;
  /** When false, Enter shortcuts do nothing. Defaults to true. */
  enabled?: boolean;
  /** When true, focuses the container on mount. Defaults to true. */
  focusOnMount?: boolean;
};

/**
 * Controls (and MUI field chrome) that should keep their own focus on pointer down.
 * Includes InputBase/FormControl so clicks on outlined padding still focus the field.
 */
const INTERACTIVE_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  'a',
  'label',
  '[contenteditable="true"]',
  '[role="button"]',
  '.MuiInputBase-root',
  '.MuiFormControl-root',
  '.MuiButtonBase-root',
  '.MuiAutocomplete-root',
  '.MuiChip-root',
].join(', ');

/**
 * Focus management + enter-to-submit keydown for a panel-like container.
 * Keeps shortcuts working when focus is on the panel rather than a field.
 */
export function useEnterToSubmitFocusContainer({
  mode = 'auto',
  enabled = true,
  focusOnMount = true,
}: Options = {}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onKeyDown = useEnterToSubmit({ mode, enabled });

  useEffect(() => {
    if (!focusOnMount) {
      return;
    }

    ref.current?.focus();
  }, [focusOnMount]);

  /**
   * Restores panel focus when clicking non-interactive chrome so Cmd/Ctrl+Enter
   * still reaches the enter-to-submit handler after leaving a field.
   */
  function onMouseDown(event: MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }

    ref.current?.focus();
  }

  return {
    ref,
    tabIndex: -1 as const,
    onKeyDown,
    onMouseDown,
  };
}
